import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface SeniorExitIntentPopupProps {
  sessionKey?: string;
}

export default function SeniorExitIntentPopup({
  sessionKey = 'seniorExitIntentShown',
}: SeniorExitIntentPopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  const triggerPopup = useCallback(() => {
    if (hasShown) return;
    if (sessionStorage.getItem(sessionKey)) return;
    setIsVisible(true);
    setHasShown(true);
    sessionStorage.setItem(sessionKey, 'true');
  }, [hasShown, sessionKey]);

  useEffect(() => {
    if (sessionStorage.getItem(sessionKey)) return;

    // Exit-intent: mouse heading toward browser tab bar
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 8) {
        triggerPopup();
      }
    };

    // Fallback: 40-second inactivity timer
    let inactivityTimer: ReturnType<typeof setTimeout>;
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(triggerPopup, 40000);
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVisible(false);
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mousemove', resetTimer);
    document.addEventListener('scroll', resetTimer);
    document.addEventListener('keydown', handleEscape);
    resetTimer();

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mousemove', resetTimer);
      document.removeEventListener('scroll', resetTimer);
      document.removeEventListener('keydown', handleEscape);
      clearTimeout(inactivityTimer);
    };
  }, [triggerPopup, sessionKey]);

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4"
      style={{ animation: 'seniorFadeIn 0.25s ease' }}
      onClick={() => setIsVisible(false)}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg relative overflow-hidden"
        style={{ animation: 'seniorSlideUp 0.3s ease' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#1A3A52] via-[#D97642] to-[#1A3A52]" />

        {/* Close button */}
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          aria-label="Close"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="px-8 pt-7 pb-8">
          {/* Header */}
          <div className="flex items-start gap-4 mb-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#D97642]/10 rounded-xl flex-shrink-0">
              <i className="ri-shield-check-line text-3xl text-[#D97642]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#1A3A52] leading-tight">
                Before You Go — Find Your Plan in 60 Seconds
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Coverage available ages 50–85 · No exam required
              </p>
            </div>
          </div>

          {/* Hero image */}
          <div className="w-full h-36 rounded-xl overflow-hidden mb-5">
            <img
              src="https://public.readdy.ai/ai/img_res/ab2c250ea246c7687767f71a83e891b9.jpg"
              alt="Senior couple reviewing life insurance options in Indiana"
              title="Final Expense Life Insurance for Indiana Seniors — Hoosier Life Insurance"
              loading="lazy"
              decoding="async"
              width={600}
              height={200}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Body copy */}
          <p className="text-base text-gray-700 leading-relaxed mb-5">
            Thousands of Indiana seniors ages 50–85 have locked in guaranteed coverage — <strong>no medical exam, no doctor visit, no waiting period.</strong> Rates start as low as <strong>$18/month.</strong>
          </p>

          {/* Trust checkmarks */}
          <ul className="space-y-2 mb-6">
            {[
              'Guaranteed acceptance for ages 50–85',
              'No medical exam or health questions',
              'Coverage starts the same day you apply',
              'Licensed Indiana agent — no pressure',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2.5 text-sm text-gray-700">
                <span className="w-5 h-5 flex items-center justify-center bg-[#D97642]/10 rounded-full flex-shrink-0">
                  <i className="ri-check-line text-[#D97642] text-xs" />
                </span>
                {item}
              </li>
            ))}
          </ul>

          {/* Primary CTA */}
          <Link
            to="/book-appointment"
            onClick={() => setIsVisible(false)}
            className="block w-full text-center bg-[#D97642] text-white py-3.5 rounded-xl font-bold text-base hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap mb-3"
          >
            <i className="ri-map-pin-line mr-2" />
            Find My Plan — Free
          </Link>

          {/* Secondary CTA */}
          <a
            href="tel:3176482983"
            onClick={() => setIsVisible(false)}
            className="block w-full text-center bg-[#1A3A52] text-white py-3.5 rounded-xl font-bold text-base hover:bg-[#0F2A3F] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line mr-2" />
            Call (317) 648-2983
          </a>

          <p className="text-[10px] text-gray-400 text-center mt-3">
            Speak with a licensed Indiana agent who specializes in senior coverage. No spam, no obligation.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes seniorFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes seniorSlideUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
